import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Check, ChevronRight, FileText, Link2, Plus, Sparkles, Target, X } from 'lucide-react';
import type { OrgMember } from '../../../hooks/useOrgMembers';
import { getRoles } from '../../../services/organizationStructureService';
import type { LstTemplateInput } from '../services/lstService';
import { EMPTY_SWI, type SwiFormState } from './SwiConfigSection';
import type { Indicator } from '../../../types/indicator';
import {
  LST_ACTIVITY_KIND_LABELS,
  LST_ALERT_CHANNEL_LABELS,
  LST_ALERT_LEAD_OPTIONS,
  LST_CARGO_LABELS,
  LST_CATEGORY_LABELS,
  LST_CATEGORY_ORDER,
  LST_FREQUENCY_LABELS,
  LST_PERIOD_LABELS,
  LST_PERIOD_ORDER,
  resolveLstCargoFromContext,
  type LstActivityKind,
  type LstAlertChannel,
  type LstCargo,
  type LstCategory,
  type LstFrequency,
  type LstPeriod,
  type LstTemplate,
} from '../types';
import type { LstUserContext } from '../hooks/useLstUserContext';
import { useCurrentWeek } from '../hooks/useLstData';
import { computeTplProjectMatrix } from '../services/tplProjectMatrixInsights';
import { buildDataflowExecutiveRibbon } from '../services/dataflowExecutiveDiagnostics';
import { DataflowExecutiveRibbon } from './dataflow/DataflowExecutiveRibbon';
import { formatTimeHHmm } from '../services/lstMatrixHourly';
import type { IndicatorLinkValue } from './IndicatorImpactCombobox';
import { LeanLibraryModal } from './tpl/LeanLibraryModal';
import { TplPokaYokeDialog } from './tpl/TplPokaYokeDialog';
import { TplAiSuggestModal } from './tpl/TplAiSuggestModal';
import { leanItemToTemplatePayload, type LeanLibraryItem } from '../types/leanLibrary';
import { getDirectReports } from '../services/lstHierarchy';
import type { TplPokaYokeAnswers } from '../services/tplPokaYoke';
import { ConfigRightPanel, type ConfigPanelView } from './ConfigRightPanel';
import {
  formatConfigScopeSummary,
  getManageableLstCargos,
} from '../utils/lstCargoGovernance';
import {
  buildRoleNameByRoleId,
  createMemberRoleNameResolver,
  filterMembersForTemplateScope,
  filterTemplatesForConfigScope,
} from '../utils/lstTemplateScope';
import { hasSwiPatternContent } from '../utils/swiContent';

interface DepartmentOption {
  id: string;
  name: string;
}

interface ConfiguracaoPanelProps {
  organizationId: string;
  templates: LstTemplate[];
  members: OrgMember[];
  indicators: Indicator[];
  departments: DepartmentOption[];
  currentUserId: string;
  userContext: LstUserContext;
  savingTemplate: boolean;
  savingLink: string | null;
  onCreate: (input: LstTemplateInput) => Promise<boolean>;
  onUpdate: (id: string, patch: Partial<LstTemplateInput>) => Promise<boolean>;
  onDeactivate: (id: string) => Promise<boolean>;
  onSetReportsTo: (userId: string, reportsToUserId: string | null) => Promise<boolean>;
  isAdmin?: boolean;
}

const FREQS: LstFrequency[] = ['horaria', 'diaria', 'semanal', 'mensal'];

interface FormState {
  id: string | null;
  title: string;
  scheduled_time: string;
  period: LstPeriod;
  frequency: LstFrequency;
  category: LstCategory;
  is_critical: boolean;
  duration_minutes: number;
  alert_lead_minutes: 5 | 10 | 15;
  alert_channel: LstAlertChannel;
  indicatorLink: IndicatorLinkValue;
  activity_kind: LstActivityKind;
  swi: SwiFormState;
}

const EMPTY_FORM: FormState = {
  id: null,
  title: '',
  scheduled_time: '',
  period: 'inicio',
  frequency: 'diaria',
  category: 'producao',
  is_critical: false,
  duration_minutes: 15,
  alert_lead_minutes: 10,
  alert_channel: 'andon',
  indicatorLink: {
    linked_indicator_id: null,
    linked_indicator_name: null,
    linked_indicator_target: null,
  },
  activity_kind: 'standard',
  swi: { ...EMPTY_SWI },
};

const inputCls =
  'h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none transition-colors focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200';
const labelCls = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400';

const pickerBtnCls =
  'flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-left text-[12px] transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-slate-600';

export const ConfiguracaoPanel: React.FC<ConfiguracaoPanelProps> = ({
  organizationId,
  templates,
  members,
  indicators,
  departments,
  currentUserId,
  userContext,
  savingTemplate,
  savingLink,
  onCreate,
  onUpdate,
  onDeactivate,
  onSetReportsTo,
  isAdmin = false,
}) => {
  const actorCargo = useMemo(
    () => resolveLstCargoFromContext(userContext.lstCargo, userContext.roleName),
    [userContext.lstCargo, userContext.roleName],
  );

  const manageableCargos = useMemo(
    () => getManageableLstCargos(actorCargo, userContext.roleName, { isAdmin }),
    [actorCargo, userContext.roleName, isAdmin],
  );

  const [selectedCargo, setSelectedCargo] = useState<LstCargo>(actorCargo);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedExceptionUserId, setSelectedExceptionUserId] = useState('');
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [rightView, setRightView] = useState<ConfigPanelView>('activities');
  const [swiFocusRequest, setSwiFocusRequest] = useState(0);
  const activityTitleRef = useRef<HTMLInputElement>(null);
  const leftFormScrollRef = useRef<HTMLDivElement>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [pokaOpen, setPokaOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [applyingAi, setApplyingAi] = useState(false);

  const defaultDepartmentId = useMemo(() => {
    if (!userContext.departmentName) return '';
    return departments.find((d) => d.name === userContext.departmentName)?.id ?? '';
  }, [departments, userContext.departmentName]);

  useEffect(() => {
    if (manageableCargos.includes(actorCargo)) {
      setSelectedCargo(actorCargo);
    } else if (manageableCargos[0]) {
      setSelectedCargo(manageableCargos[0]);
    }
  }, [actorCargo, manageableCargos]);

  useEffect(() => {
    setSelectedDepartmentId((prev) => prev || defaultDepartmentId);
  }, [defaultDepartmentId]);

  const selectedDepartmentName = useMemo(
    () => departments.find((d) => d.id === selectedDepartmentId)?.name ?? null,
    [departments, selectedDepartmentId],
  );

  const { data: orgRoles = [] } = useQuery({
    queryKey: ['lst', 'config-roles', organizationId],
    queryFn: () => getRoles(organizationId!),
    enabled: !!organizationId,
    staleTime: 300_000,
  });

  const resolveMemberRoleName = useMemo(
    () => createMemberRoleNameResolver(buildRoleNameByRoleId(orgRoles)),
    [orgRoles],
  );

  const scopedMembers = useMemo(
    () =>
      filterMembersForTemplateScope(
        members,
        selectedCargo,
        selectedDepartmentId || undefined,
        resolveMemberRoleName,
      ),
    [members, selectedCargo, selectedDepartmentId, resolveMemberRoleName],
  );

  const listContextUserId = selectedExceptionUserId || undefined;

  const scopedTemplates = useMemo(
    () =>
      filterTemplatesForConfigScope(templates, selectedCargo, {
        departmentId: selectedDepartmentId || undefined,
        members,
        contextUserId: listContextUserId,
      }),
    [templates, selectedCargo, selectedDepartmentId, members, listContextUserId],
  );

  useEffect(() => {
    if (!selectedExceptionUserId) return;
    if (!scopedMembers.some((m) => m.id === selectedExceptionUserId)) {
      setSelectedExceptionUserId('');
    }
  }, [scopedMembers, selectedExceptionUserId]);

  const scopeSummary = useMemo(
    () => formatConfigScopeSummary(scopedTemplates, selectedCargo, selectedDepartmentName),
    [scopedTemplates, selectedCargo, selectedDepartmentName],
  );

  const week = useCurrentWeek();
  const projectMatrix = useMemo(
    () => computeTplProjectMatrix(scopedTemplates, week.dates),
    [scopedTemplates, week.dates],
  );

  const engineeringRibbon = useMemo(
    () =>
      buildDataflowExecutiveRibbon({
        ielScore: 100,
        maturityScore: Math.min(100, projectMatrix.header.totalRotinas * 5),
        disciplinaPct: 100,
        riskCount: projectMatrix.header.conflictingCount,
        lossCount: 0,
        actionCount: projectMatrix.header.criticalCount,
        plannerConflicts: projectMatrix.header.conflictingCount,
      }),
    [projectMatrix],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const isReuniao = form.activity_kind === 'reuniao_camada';
  const activityTitleReady = form.title.trim().length > 0;

  const indicatorLinked = !!(
    form.indicatorLink.linked_indicator_id || form.indicatorLink.linked_indicator_name?.trim()
  );
  const indicatorMetaValid =
    !indicatorLinked ||
    (form.indicatorLink.linked_indicator_target != null && form.indicatorLink.linked_indicator_target > 0);

  const subordinateCount = useMemo(
    () => getDirectReports(currentUserId, members).length,
    [currentUserId, members],
  );

  const indicatorSummary = useMemo(() => {
    if (!indicatorLinked) return 'Nenhum indicador vinculado';
    const name = form.indicatorLink.linked_indicator_id
      ? indicators.find((i) => i.id === form.indicatorLink.linked_indicator_id)?.name
      : form.indicatorLink.linked_indicator_name;
    const meta = form.indicatorLink.linked_indicator_target;
    return meta != null ? `${name ?? 'KPI'} · meta ${meta}` : (name ?? 'KPI');
  }, [form.indicatorLink, indicatorLinked, indicators]);

  const swiShortcutLabel = useMemo(() => {
    if (hasSwiPatternContent(form.swi)) {
      return '✓ Instrução de Trabalho Padrão Anexada';
    }
    return 'Instrução de Trabalho Padrão (SWI) — Clique para editar ou colar o padrão…';
  }, [form.swi]);

  const hasSwiContent = useMemo(() => hasSwiPatternContent(form.swi), [form.swi]);

  const reset = useCallback(() => setForm({ ...EMPTY_FORM }), []);

  const handleScopeCargoChange = (cargo: LstCargo) => {
    setSelectedCargo(cargo);
    setSelectedExceptionUserId('');
    reset();
    setRightView('activities');
  };

  const handleScopeDepartmentChange = (departmentId: string) => {
    setSelectedDepartmentId(departmentId);
    setSelectedExceptionUserId('');
  };

  const buildPayload = useCallback((): LstTemplateInput | null => {
    if (form.title.trim().length < 2 || !indicatorMetaValid) return null;
    const scheduledTime = formatTimeHHmm(form.scheduled_time) ?? (form.scheduled_time || null);
    return {
      organization_id: '',
      target_role: selectedCargo,
      scope: selectedExceptionUserId ? 'user' : 'cargo',
      assigned_user_id: selectedExceptionUserId || null,
      title: form.title.trim(),
      scheduled_time: scheduledTime,
      period: form.period,
      frequency: form.frequency,
      category: form.category,
      is_critical: form.is_critical,
      swi_mode: isReuniao ? 'text' : form.swi.swi_mode,
      swi_text: isReuniao ? null : form.swi.swi_text.trim() || null,
      swi_doc_url: isReuniao ? null : form.swi.swi_doc_url || null,
      swi_checklist: isReuniao ? [] : form.swi.swi_checklist.filter((i) => i.text.trim()),
      description: isReuniao ? null : form.swi.swi_text.trim() || null,
      duration_minutes: isReuniao ? undefined : form.duration_minutes,
      alert_lead_minutes: form.alert_lead_minutes,
      alert_channel: form.alert_channel,
      linked_indicator_id: form.indicatorLink.linked_indicator_id,
      linked_indicator_name: form.indicatorLink.linked_indicator_name,
      linked_indicator_target: form.indicatorLink.linked_indicator_target,
      activity_kind: form.activity_kind,
    };
  }, [form, indicatorMetaValid, isReuniao, selectedCargo, selectedExceptionUserId]);

  const persistForm = async () => {
    const payload = buildPayload();
    if (!payload) return;
    if (payload.scope === 'user' && !payload.assigned_user_id) return;
    const ok = form.id ? await onUpdate(form.id, payload) : await onCreate(payload);
    if (ok) {
      reset();
      setRightView('activities');
    }
  };

  const submit = () => {
    if (!buildPayload()) return;
    setPokaOpen(true);
  };

  const handlePokaConfirm = async (_answers: TplPokaYokeAnswers, _force: boolean) => {
    setPokaOpen(false);
    await persistForm();
  };

  const startEdit = useCallback((t: LstTemplate) => {
    if (t.target_role !== selectedCargo) setSelectedCargo(t.target_role);
    setSelectedExceptionUserId(t.scope === 'user' ? t.assigned_user_id ?? '' : '');
    setForm({
      id: t.id,
      title: t.title,
      scheduled_time: formatTimeHHmm(t.scheduled_time) ?? '',
      period: t.period,
      frequency: t.frequency,
      category: t.category ?? 'producao',
      is_critical: t.is_critical,
      duration_minutes: t.duration_minutes ?? 15,
      alert_lead_minutes: t.alert_lead_minutes ?? 10,
      alert_channel: t.alert_channel ?? 'andon',
      indicatorLink: {
        linked_indicator_id: t.linked_indicator_id ?? null,
        linked_indicator_name: t.linked_indicator_name ?? null,
        linked_indicator_target: t.linked_indicator_target ?? null,
      },
      activity_kind: t.activity_kind ?? 'standard',
      swi: {
        swi_mode: t.swi_mode ?? 'text',
        swi_text: t.swi_text ?? t.description ?? '',
        swi_doc_url: t.swi_doc_url ?? '',
        swi_checklist: t.swi_checklist ?? [],
      },
    });
    setRightView('activities');
  }, [selectedCargo]);

  /** Autofill a partir da Biblioteca Lean — gestor define apenas horário e salva. */
  const handleLibrarySelect = (item: LeanLibraryItem) => {
    const payload = leanItemToTemplatePayload(item);
    setForm({
      ...EMPTY_FORM,
      title: payload.title,
      duration_minutes: payload.duration_minutes,
      frequency: payload.frequency,
      category: payload.category,
      activity_kind: payload.activity_kind,
      is_critical: payload.is_critical,
      scheduled_time: '',
      indicatorLink: {
        linked_indicator_id: null,
        linked_indicator_name: payload.linked_indicator_name,
        linked_indicator_target: null,
      },
      swi: {
        ...EMPTY_SWI,
        swi_mode: 'text',
        swi_text: payload.swi_text,
        swi_checklist: payload.swi_checklist,
      },
    });
    setLibraryOpen(false);
    setRightView('swi');
    setSwiFocusRequest((n) => n + 1);
  };

  const handleAiApply = async (suggestions: Partial<LstTemplateInput>[]) => {
    setApplyingAi(true);
    try {
      for (const s of suggestions) {
        const payload: LstTemplateInput = {
          organization_id: '',
          target_role: selectedCargo,
          scope: selectedExceptionUserId ? 'user' : 'cargo',
          assigned_user_id: selectedExceptionUserId || null,
          title: s.title ?? 'Rotina',
          scheduled_time: s.scheduled_time ?? '08:00',
          period: 'inicio',
          frequency: s.frequency ?? 'diaria',
          category: s.category ?? 'producao',
          is_critical: s.is_critical ?? false,
          swi_mode: 'text',
          swi_text: s.swi_text ?? null,
          swi_doc_url: null,
          swi_checklist: [],
          description: s.description ?? null,
          duration_minutes: s.duration_minutes ?? 15,
          alert_lead_minutes: 10,
          alert_channel: 'andon',
          linked_indicator_id: null,
          linked_indicator_name: null,
          linked_indicator_target: null,
          activity_kind: s.activity_kind ?? 'standard',
        };
        await onCreate(payload);
      }
      setAiOpen(false);
      setRightView('activities');
    } finally {
      setApplyingAi(false);
    }
  };

  const openPanel = (view: ConfigPanelView) => setRightView(view);

  const openSwiPanel = () => {
    if (isReuniao) return;
    setRightView('swi');
    setSwiFocusRequest((n) => n + 1);
  };

  const handleBindSwiPattern = useCallback(() => {
    setRightView('activities');
    requestAnimationFrame(() => {
      leftFormScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      activityTitleRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const cargoSelectDisabled = manageableCargos.length <= 1;

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-[var(--bg-surface)]">
      <header className="shrink-0 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3 dark:border-slate-800 dark:from-slate-900/60 dark:to-slate-950">
        <p className="text-[9px] font-bold uppercase tracking-widest text-[#2F6FED]">Projeto do Trabalho Padrão</p>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
            Cargo: {LST_CARGO_LABELS[selectedCargo]}
          </span>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
            Setor: {selectedDepartmentName ?? 'Todos'}
          </span>
        </div>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold uppercase tracking-wide tabular-nums text-slate-700 dark:text-slate-200">
          <span>{projectMatrix.header.totalRotinas} rotinas</span>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <span className={projectMatrix.header.conflictingCount > 0 ? 'text-red-600' : ''}>
            {projectMatrix.header.conflictingCount} conflitos
          </span>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <span className={projectMatrix.header.criticalCount > 0 ? 'text-amber-600' : ''}>
            {projectMatrix.header.criticalCount} crítica{projectMatrix.header.criticalCount !== 1 ? 's' : ''}
          </span>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <span className={projectMatrix.header.indicatorLinkedCount === 0 ? 'text-amber-600' : ''}>
            {projectMatrix.header.indicatorLinkedCount} KPIs vinculados
          </span>
        </p>
        <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
          {projectMatrix.header.minutesWeek} min/semana · {projectMatrix.header.withoutIndicatorCount} sem indicador
        </p>
        <DataflowExecutiveRibbon data={engineeringRibbon} compact className="mt-2" />
      </header>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(300px,400px)]">
        <div className="flex min-h-0 flex-col overflow-hidden">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2 dark:border-slate-800">
            <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
              {form.id ? 'Editando rotina' : 'Nova rotina'}
            </p>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setLibraryOpen(true)}
                className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-700 hover:bg-blue-100"
              >
                <BookOpen size={12} /> Biblioteca Lean
              </button>
              <button
                type="button"
                onClick={() => setAiOpen(true)}
                className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-[10px] font-semibold text-violet-700 hover:bg-violet-100"
              >
                <Sparkles size={12} /> Sugerir TPL
              </button>
            </div>
          </div>

          <div ref={leftFormScrollRef} className="min-h-0 flex-1 overflow-y-auto bg-slate-50/40 p-4 dark:bg-slate-900/20">
            {/* Escopo organizacional */}
            <fieldset className="mb-4 rounded-lg border border-slate-200/80 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/30">
              <legend className="px-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Escopo organizacional
              </legend>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Cargo (escopo TPL)</label>
                <select
                  className={inputCls}
                  value={selectedCargo}
                  disabled={cargoSelectDisabled || userContext.loading}
                  onChange={(e) => handleScopeCargoChange(e.target.value as LstCargo)}
                >
                  {manageableCargos.map((c) => (
                    <option key={c} value={c}>
                      {LST_CARGO_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Setor (contexto)</label>
                <select
                  className={inputCls}
                  value={selectedDepartmentId}
                  disabled={userContext.loading}
                  onChange={(e) => handleScopeDepartmentChange(e.target.value)}
                >
                  <option value="">Todos os setores</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Usuário (exceção)</label>
                <select
                  className={inputCls}
                  value={selectedExceptionUserId}
                  disabled={userContext.loading || scopedMembers.length === 0}
                  onChange={(e) => setSelectedExceptionUserId(e.target.value)}
                >
                  <option value="">— Padrão do cargo (todos) —</option>
                  {scopedMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-slate-400">
                  {selectedExceptionUserId
                    ? 'Exceção individual — rotina exclusiva na agenda desta pessoa.'
                    : 'Vazio = rotina global para todos os profissionais do cargo.'}
                </p>
              </div>
            </div>
            </fieldset>

            <fieldset className="mb-4 rounded-lg border border-slate-200/80 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/30">
              <legend className="px-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Definição da rotina
              </legend>
              <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className={labelCls}>Atividade</label>
                <input
                  ref={activityTitleRef}
                  className={inputCls}
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  onFocus={() => openPanel('activities')}
                  placeholder="Ex.: Auditoria de 5S"
                />
              </div>

              <div className="col-span-2 grid grid-cols-2 gap-3 lg:grid-cols-3">
                <div>
                  <label className={labelCls}>Frequência</label>
                  <select className={inputCls} value={form.frequency} onChange={(e) => set('frequency', e.target.value as LstFrequency)}>
                    {FREQS.map((f) => (
                      <option key={f} value={f}>{LST_FREQUENCY_LABELS[f]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Horário inicial</label>
                  <input
                    type="time"
                    className={inputCls}
                    value={form.scheduled_time}
                    onChange={(e) => set('scheduled_time', e.target.value)}
                  />
                </div>
                {!isReuniao && (
                  <div>
                    <label className={labelCls}>Duração (min)</label>
                    <input
                      type="number"
                      min={5}
                      max={240}
                      step={5}
                      className={inputCls}
                      value={form.duration_minutes}
                      onChange={(e) => set('duration_minutes', Math.max(5, Number(e.target.value) || 15))}
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Alerta</label>
                  <select
                    className={inputCls}
                    value={form.alert_lead_minutes}
                    onChange={(e) => set('alert_lead_minutes', Number(e.target.value) as 5 | 10 | 15)}
                  >
                    {LST_ALERT_LEAD_OPTIONS.map((m) => (
                      <option key={m} value={m}>{m} min antes</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Categoria</label>
                  <select className={inputCls} value={form.category} onChange={(e) => set('category', e.target.value as LstCategory)}>
                    {LST_CATEGORY_ORDER.map((c) => (
                      <option key={c} value={c}>{LST_CATEGORY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Bloco do turno</label>
                  <select className={inputCls} value={form.period} onChange={(e) => set('period', e.target.value as LstPeriod)}>
                    {LST_PERIOD_ORDER.map((p) => (
                      <option key={p} value={p}>{LST_PERIOD_LABELS[p]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Forma de aviso</label>
                  <select
                    className={inputCls}
                    value={form.alert_channel}
                    onChange={(e) => set('alert_channel', e.target.value as LstAlertChannel)}
                  >
                    {(Object.keys(LST_ALERT_CHANNEL_LABELS) as LstAlertChannel[]).map((ch) => (
                      <option key={ch} value={ch}>{LST_ALERT_CHANNEL_LABELS[ch]}</option>
                    ))}
                  </select>
                </div>
              </div>
              </div>
            </fieldset>

            <fieldset className="mb-4 rounded-lg border border-slate-200/80 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/30">
              <legend className="px-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Padrões e vínculos
              </legend>
              <div className="grid grid-cols-2 gap-4">

              {!isReuniao && (
                <div className="col-span-2">
                  <label className={labelCls}>Instrução de trabalho</label>
                  <button
                    type="button"
                    onClick={openSwiPanel}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] transition-colors ${
                      hasSwiContent
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50'
                        : rightView === 'swi'
                          ? 'border-blue-300 bg-white ring-1 ring-blue-200 hover:bg-slate-50 dark:border-blue-700 dark:bg-slate-900/40 dark:ring-blue-900/50'
                          : `${pickerBtnCls}`
                    }`}
                  >
                    <span
                      className={`min-w-0 flex-1 truncate ${
                        hasSwiContent ? 'font-medium' : 'text-slate-400'
                      }`}
                    >
                      <FileText
                        size={12}
                        className={`mr-1.5 inline shrink-0 ${hasSwiContent ? 'text-emerald-600 dark:text-emerald-400' : 'text-indigo-500'}`}
                      />
                      {swiShortcutLabel}
                    </span>
                    <ChevronRight size={14} className="shrink-0 text-slate-400" />
                  </button>
                </div>
              )}

              <div className="col-span-2">
                <label className={labelCls}>Indicador de impacto</label>
                <button type="button" className={pickerBtnCls} onClick={() => openPanel('indicator')}>
                  <span className={`truncate ${indicatorLinked ? 'text-slate-800 dark:text-slate-100' : 'text-slate-400'}`}>
                    <Target size={12} className="mr-1.5 inline text-blue-500" />
                    {indicatorSummary}
                  </span>
                  <ChevronRight size={14} className="shrink-0 text-slate-400" />
                </button>
              </div>

              <div className="col-span-2">
                <label className={labelCls}>Cadeia de ajuda / Gestor</label>
                <button type="button" className={pickerBtnCls} onClick={() => openPanel('manager')}>
                  <span className="text-slate-600 dark:text-slate-300">
                    <Link2 size={12} className="mr-1.5 inline text-emerald-500" />
                    Configurar gestor direto e exceções
                  </span>
                  <ChevronRight size={14} className="shrink-0 text-slate-400" />
                </button>
              </div>

              <div className="col-span-2">
                <label className={labelCls}>Tipo de atividade</label>
                <select
                  className={inputCls}
                  value={form.activity_kind}
                  onChange={(e) => set('activity_kind', e.target.value as LstActivityKind)}
                >
                  {(Object.keys(LST_ACTIVITY_KIND_LABELS) as LstActivityKind[]).map((k) => (
                    <option key={k} value={k}>{LST_ACTIVITY_KIND_LABELS[k]}</option>
                  ))}
                </select>
                {isReuniao && (
                  <p className="mt-0.5 text-[9px] text-violet-500">
                    Reunião de Camada: duração e SOP são registrados no drawer de Ata em tempo real.
                  </p>
                )}
              </div>

              <label className="col-span-2 flex cursor-pointer items-center gap-1.5">
                <input type="checkbox" className="h-3.5 w-3.5 accent-red-600" checked={form.is_critical} onChange={(e) => set('is_critical', e.target.checked)} />
                <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">Rotina crítica</span>
              </label>

            </div>
            </fieldset>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 px-4 py-3 dark:border-slate-800">
            {form.id && (
              <button type="button" onClick={reset} className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                <X size={13} /> Cancelar
              </button>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={savingTemplate || form.title.trim().length < 2 || !indicatorMetaValid}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#2F6FED] px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {form.id ? <Check size={14} /> : <Plus size={14} />}
              {form.id ? 'Salvar Alterações' : 'Adicionar rotina'}
            </button>
          </div>
        </div>

        <ConfigRightPanel
          view={rightView}
          onViewChange={setRightView}
          scopeLabel={scopeSummary}
          weekDates={week.dates}
          templates={scopedTemplates}
          contextUserId={selectedExceptionUserId || undefined}
          selectedTemplateId={form.id}
          onSelectTemplate={startEdit}
          onDeactivateTemplate={onDeactivate}
          members={members}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          savingLink={savingLink}
          onSetReportsTo={onSetReportsTo}
          indicators={indicators}
          indicatorLink={form.indicatorLink}
          onIndicatorChange={(indicatorLink) => set('indicatorLink', indicatorLink)}
          swiEnabled={!isReuniao}
          organizationId={organizationId}
          editingTemplateId={form.id}
          swi={form.swi}
          onSwiChange={(patch) => setForm((prev) => ({ ...prev, swi: { ...prev.swi, ...patch } }))}
          activityTitleReady={activityTitleReady}
          editorFocusRequest={swiFocusRequest}
          onBindSwiPattern={handleBindSwiPattern}
        />
      </div>

      <LeanLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        lstCargo={selectedCargo}
        roleName={LST_CARGO_LABELS[selectedCargo]}
        onSelect={handleLibrarySelect}
      />

      <TplPokaYokeDialog
        open={pokaOpen}
        activityTitle={form.title || 'Nova rotina'}
        onCancel={() => setPokaOpen(false)}
        onConfirm={handlePokaConfirm}
      />

      <TplAiSuggestModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        context={{
          lstCargo: selectedCargo,
          roleName: LST_CARGO_LABELS[selectedCargo],
          subordinateCount,
          operationComplexity: 'media',
          existingTitles: scopedTemplates.map((t) => t.title),
        }}
        existingTemplates={scopedTemplates}
        onApply={handleAiApply}
        applying={applyingAi}
      />
    </section>
  );
};

export default ConfiguracaoPanel;
